import {
  type Active,
  DndContext as DragAndDropContext,
  type DragOverEvent,
  DragOverlay,
  MouseSensor,
  type Over,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import clsx from 'clsx';
import {
  type JSX,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  createHeaderPlaceholder,
  createRestrictColumnDragAndDrop,
  customCollisionDetection,
  getCustomScrollSpeed,
  getDraggableAreaBounds,
  getDraggedHeaderElement,
  indicateInsertPosition,
  SCROLL_PROXIMITY_ZONE,
  unpinnedHeaderFilter,
  updateColumnOrderOnDragEnd,
} from './column-order-dnd-utils.js';
import * as styles from './ColumnOrder.sty.js';
import { getDataTableBorderWidth } from '../../../utils/data-table-border-width.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import type { Column } from '../../hooks/useTable/types.js';

const DRAGGED_HEADER_DEFAULT_WIDTH = 100;
const DRAGGED_HEADER_DEFAULT_HEIGHT = 37;

export function ColumnOrderDragAndDropContext({
  children,
}: Readonly<{
  children: ReactNode;
}>): JSX.Element {
  const table = useDataTable();
  const totalHeaderHeight = table.getTotalHeaderHeight();
  const visualVariantOptions = table.getVisualVariantOptions();
  const containedVariant = visualVariantOptions.contained;
  const hasSubHeaders = table.getHeaderGroups().length > 1;
  const verticalDividers =
    visualVariantOptions.verticalDividers || hasSubHeaders;
  const isColumnPinningEnabled = table.options.enableColumnPinning;
  const pinnedColumns = table.getState().columnPinning;

  const virtualizationContainer = table.getRowVirtualizationContainer();
  const [dragOverlayContent, setDragOverlayContent] =
    useState<RefObject<HTMLDivElement> | null>(null);
  // relevant only if column pinning is enabled
  const pinnedDraggableAreaBoundsRef = useRef<
    ReturnType<typeof getDraggableAreaBounds>
  >({ left: undefined, right: undefined });
  const [draggedHeaderSize, setDraggedHeaderSize] = useState<{
    width: number;
    height: number;
  }>({
    width: DRAGGED_HEADER_DEFAULT_WIDTH,
    height: DRAGGED_HEADER_DEFAULT_HEIGHT,
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
  );

  const restrictColumnDragAndDrop = useMemo(
    () =>
      createRestrictColumnDragAndDrop(
        totalHeaderHeight,
        containedVariant,
        pinnedColumns,
        hasSubHeaders,
        virtualizationContainer,
      ),
    [
      totalHeaderHeight,
      virtualizationContainer,
      containedVariant,
      pinnedColumns,
      hasSubHeaders,
    ],
  );

  const placeholderElement = useRef<HTMLElement | null>(null);

  // Since we disable autoScroll when column pinning is enabled, we drive scrolling from
  // a continuous RAF(requestAnimationFrame) loop. Using a ref prevents scroll updates from causing re-renders.
  const scrollFrameRef = useRef<number | null>(null);

  // Track raw pointer position to determine scroll direction.
  // Using a pointermove listener (rather than dnd-kit's `delta`) is necessary
  // because dnd-kit adds scroll compensation to `delta.x` as the container
  // scrolls, which would cause the right-scroll zone to never exit.
  // relevant only if column pinning is enabled
  const pointerXRef = useRef<number>(0);
  const trackPointerXRef = useRef<((e: PointerEvent) => void) | null>(null);
  // hold the parent header DOM element when dragging a child column so the scroll loop
  // can check whether the parent is already fully visible and there's no need for scrolling
  const parentHeaderElementRef = useRef<HTMLElement | null>(null);

  const stopScrollLoop = useCallback(() => {
    if (scrollFrameRef.current !== null) {
      cancelAnimationFrame(scrollFrameRef.current);
      scrollFrameRef.current = null;
    }
  }, []);

  const startScrollLoop = useCallback(() => {
    if (scrollFrameRef.current !== null) {
      return;
    }

    const loop = () => {
      if (!virtualizationContainer) {
        scrollFrameRef.current = null;
        return;
      }

      // Read pointer position fresh every frame. RAF callbacks always run after
      // all pending input events (including pointermove) have been processed,
      // so pointerXRef.current is guaranteed to hold the latest position here.
      const mouseX = pointerXRef.current;
      const containerRect = virtualizationContainer.getBoundingClientRect();
      const rightBound =
        pinnedDraggableAreaBoundsRef.current.right ?? containerRect.right;
      const leftBound =
        pinnedDraggableAreaBoundsRef.current.left ?? containerRect.left;

      // Positive penetration means the cursor has entered the proximity zone.
      // Zero or negative means it is safely away from that boundary.
      const rightPenetration = mouseX - (rightBound - SCROLL_PROXIMITY_ZONE);
      const leftPenetration = leftBound + SCROLL_PROXIMITY_ZONE - mouseX;

      const { scrollLeft, scrollWidth, clientWidth } = virtualizationContainer;

      // When a child column is being dragged, scrolling should be capped only until the parent header becomes fully visible.
      // if the parent is already fully visible, skip scrolling entirely.
      let maxScrollRight = Infinity;
      let maxScrollLeft = Infinity;

      const parentEl = parentHeaderElementRef.current;
      if (parentEl) {
        const parentRect = parentEl.getBoundingClientRect();

        // many pixels the parent overflows past the right / left bound.
        maxScrollRight = Math.max(0, parentRect.right - rightBound);
        maxScrollLeft = Math.max(0, leftBound - parentRect.left);
      }

      if (rightPenetration > 0 && leftPenetration <= 0) {
        // Scale speed linearly from MIN at zone entry to MAX at the boundary.
        const scrollSpeed = getCustomScrollSpeed(rightPenetration);

        const canScrollRight = scrollLeft < scrollWidth - clientWidth;
        if (canScrollRight && maxScrollRight > 0) {
          // the RAF loop keeps scheduling the next frame as long as maxScrollRight > speed. Same for right scroll.
          virtualizationContainer.scrollLeft += Math.min(
            scrollSpeed,
            Math.ceil(maxScrollRight),
          );
        }
      } else if (leftPenetration > 0 && rightPenetration <= 0) {
        const scrollSpeed = getCustomScrollSpeed(leftPenetration);

        const canScrollLeft = scrollLeft > 0;
        if (canScrollLeft && maxScrollLeft > 0) {
          virtualizationContainer.scrollLeft -= Math.min(
            scrollSpeed,
            Math.ceil(maxScrollLeft),
          );
        }
      }

      scrollFrameRef.current = requestAnimationFrame(loop);
    };

    scrollFrameRef.current = requestAnimationFrame(loop);
  }, [virtualizationContainer]);

  const cleanupDragState = useCallback(() => {
    stopScrollLoop();
    parentHeaderElementRef.current = null;
    if (trackPointerXRef.current) {
      window.removeEventListener('pointermove', trackPointerXRef.current);
      trackPointerXRef.current = null;
    }
  }, [stopScrollLoop]);

  const handleDragStart = ({ active }: { active: Active }) => {
    const draggedColumn = active.data.current?.column as Column<
      unknown,
      unknown
    >;

    // Read bounds fresh from the DOM on every drag start event so they always reflect
    // the current scroll position and are never stale from an earlier render.
    if (isColumnPinningEnabled) {
      pinnedDraggableAreaBoundsRef.current = getDraggableAreaBounds(
        pinnedColumns,
        virtualizationContainer,
        hasSubHeaders ? 1 : 0,
      );

      // Start tracking raw pointer position for scroll zone detection.
      trackPointerXRef.current = (e: PointerEvent) => {
        pointerXRef.current = e.clientX;
      };
      window.addEventListener('pointermove', trackPointerXRef.current, {
        passive: true,
      });
      // If the dragged column is a child, store its parent header element so
      // the scroll loop can cap scrolling to keep the parent fully visible.
      if (draggedColumn.parent) {
        const parentId = draggedColumn.parent.id;
        parentHeaderElementRef.current =
          virtualizationContainer?.querySelector<HTMLElement>(
            `[role="columnheader"][data-header-row="0"][data-columnid=${CSS.escape(
              parentId,
            )}]${unpinnedHeaderFilter}`,
          ) ?? null;
      }

      // Start the RAF loop immediately so proximity-zone entry is detected on
      // every frame regardless of when onDragMove fires relative to pointermove.
      startScrollLoop();
    }

    table.setDraggedHeaderColumn(draggedColumn);
    setDraggedHeaderSize(table.getHeaderSize(active.id as string));
    setDragOverlayContent(active.data.current?.portalRef);

    // In order to make sure the header size does not change when the header
    // is portalled out, we create a placeholder element in the header in order
    // to preserve the grid layout
    const { headerGroupElement, draggedHeaderElement } =
      getDraggedHeaderElement(String(active.id), virtualizationContainer);

    if (
      headerGroupElement === null ||
      !(headerGroupElement instanceof HTMLElement)
    ) {
      return;
    }

    if (
      draggedHeaderElement === null ||
      !(draggedHeaderElement instanceof HTMLElement)
    ) {
      return;
    }

    // Extract the position of the dragged element and its size in order
    // to position the placeholder.
    const { gridArea } = draggedHeaderElement.style;
    const { width, height } = draggedHeaderElement.getBoundingClientRect();
    if (!gridArea) {
      return;
    }

    const verticalDividerOptions =
      verticalDividers && !draggedColumn.getIsFirstVisibleUnpinnedColumn()
        ? {
            isParentColumn:
              draggedHeaderElement.getAttribute('data-header-row') === '0',
            heightOffset:
              headerGroupElement.offsetHeight -
              draggedHeaderElement.offsetHeight,
          }
        : undefined;

    placeholderElement.current = createHeaderPlaceholder(
      gridArea,
      width,
      height,
      verticalDividerOptions,
    );
    headerGroupElement.appendChild(placeholderElement.current);
  };

  const handleDragOver = (event: DragOverEvent) => {
    indicateInsertPosition(event, virtualizationContainer);
  };

  const handleDragEnd = ({
    active,
    over,
  }: {
    active: Active;
    over: Over | null;
  }) => {
    setDragOverlayContent(null);
    cleanupDragState();
    updateColumnOrderOnDragEnd({
      active,
      over,
      table,
      virtualizationContainer,
    });
    // with nested headers, removing the placeholder element too early could lead to wrong header measurements
    // therefore, the placeholder is removed just before the next repaint
    requestAnimationFrame(() => {
      placeholderElement.current?.remove();
    });
  };

  // Clean up the RAF scroll loop and the global pointermove listener if
  // the component unmounts during an active drag (e.g. the table is removed
  // from the DOM), since handleDragEnd would never fire in that case.
  useEffect(() => {
    return () => {
      cleanupDragState();
      placeholderElement.current?.remove();
    };
  }, [stopScrollLoop, cleanupDragState]);

  return (
    <DragAndDropContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      modifiers={[restrictColumnDragAndDrop]}
      autoScroll={!isColumnPinningEnabled}
      accessibility={{ container: table.getRowVirtualizationContainer() }}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {
        // We need to portal the drag and drop overlay to escape CSS transforms on ancestor elements.
        // This ensures transforms on parent containers don't affect the position of the drag overlay.
        createPortal(
          <DragOverlay
            data-drag-overlay
            dropAnimation={null}
            className={clsx(styles.columnDndOverlay)}
            style={{
              // the observed header size includes a border which we don't want in the overlay
              height: draggedHeaderSize.height - getDataTableBorderWidth(),
              width: draggedHeaderSize.width,
            }}
          >
            <div ref={dragOverlayContent} />
          </DragOverlay>,
          document.body,
        )
      }
      {children}
    </DragAndDropContext>
  );
}
